import {
  ChevronDownIcon,
  ChevronUpIcon,
  Cross2Icon,
  HamburgerMenuIcon,
  MagnifyingGlassIcon,
} from "@radix-ui/react-icons";
import {
  Avatar,
  Box,
  Button,
  Card,
  DropdownMenu,
  Flex,
  Text,
  TextField,
} from "@radix-ui/themes";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../hooks/hooks";
import useActiveMenu from "../hooks/useActiveMenu";
import { resetPreviousPath } from "../redux/slices/previousPath";
import { resetUpdateCourseData } from "../redux/slices/school/course";
import { resetUpdateCoursePlanData } from "../redux/slices/school/coursePlan";
import { resetUpdateCourseSubjectData } from "../redux/slices/school/courseSubject";
import { resetStudentData } from "../redux/slices/school/student";
import { resetTeacherData } from "../redux/slices/school/teacher";
import { resetEntitiesData } from "../redux/slices/scientific/entities";
import { resetRoleData } from "../redux/slices/users/role";
import { resetUserData, setUserData } from "../redux/slices/users/user";
import { RootState } from "../redux/store";
import { routes } from "../routes";
import styles from "../styles/Sidebar.module.scss";
import { AUTH_LOCALSTORAGE, BASE_URL, UPLOAD_IMAGES } from "../utils/constants";
import { menuItems } from "./menuItems";
import { getUserById } from "../services/user/getUsers";
import { ExitIcon, Pencil1Icon, LockClosedIcon } from "@radix-ui/react-icons";
import toast from "react-hot-toast";
import { useIES } from "../hooks/useIES";

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { IES, isLoading } = useIES();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); // Estado para o campo de pesquisa

  const toggleSidebar = () => setIsOpen(!isOpen);
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const toggleSubMenu = (menu: string) => {
    setOpenSubMenu(openSubMenu === menu ? null : menu);
  };

  const { Fullname, Role, Permission, UserId } = useSelector(
    (state: RootState) => state.login.userData
  );

  const { isActive } = useActiveMenu();

  const handleChangePassword = () => {
    navigate(routes.changePassword);
  };

  const handleEditMyUser = async () => {
    try {
      const user = await getUserById(UserId);
      dispatch(setUserData(user));
      navigate(routes.editMyUser);
    } catch (error) {
      toast.error(
        "Erro os seus dados. Por favor, tente novamente ou consulte o administrador do sistema."
      );
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(AUTH_LOCALSTORAGE);
    window.location.reload();
  };

  const hasAnyPermission = (permissions: string[]) => {
    return permissions.some(
      (permission) => Permission && Permission.includes(permission)
    );
  };

  // Filtragem dos itens de menu com base no termo de pesquisa
  const filteredMenuItems = menuItems.filter(
    (item) =>
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.subItems &&
        item.subItems.some((subItem) =>
          subItem.title.toLowerCase().includes(searchTerm.toLowerCase())
        ))
  );

  const handleCleanReduxData = (route: string) => {
    try {
      if (location.pathname === route) {
        return;
      }
      dispatch(resetUserData());
      dispatch(resetTeacherData());
      dispatch(resetUpdateCourseData());
      dispatch(resetUpdateCourseSubjectData());
      dispatch(resetStudentData());
      dispatch(resetRoleData());
      dispatch(resetPreviousPath());
      dispatch(resetEntitiesData());
      dispatch(resetEntitiesData());
      dispatch(resetUpdateCoursePlanData());
    } catch (error) {
      console.log(error);
    }
  };
  return (
    <Box>
      <button className={styles.hamburgerButton} onClick={toggleSidebar}>
        {isOpen ? <Cross2Icon /> : <HamburgerMenuIcon />}
      </button>
      <div className={`${styles.sidebar} ${isOpen ? styles.open : ""}`}>
        <DropdownMenu.Root>
          <DropdownMenu.Trigger>
            <Button variant="surface" className={styles.fillAvailable}>
              Menu - {Fullname}
              <DropdownMenu.TriggerIcon />
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content>
            <DropdownMenu.Item onClick={handleChangePassword}>
              <LockClosedIcon style={{ marginRight: "8px" }} />
              Alterar senha
            </DropdownMenu.Item>
            <DropdownMenu.Item onClick={handleEditMyUser}>
              <Pencil1Icon style={{ marginRight: "8px" }} />
              Editar Perfil
            </DropdownMenu.Item>
            <DropdownMenu.Separator />
            <DropdownMenu.Item onClick={handleLogout}>
              <ExitIcon style={{ marginRight: "8px" }} />
              Sair
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>

        <Avatar size="6" fallback="T" radius="none" src={`${IES.LOGO}`} />
        <Box className={styles.fillAvailable}>
          <Card>
            <Flex gap="3" align="center">
              <Avatar
                size="3"
                src="png-transparent-profile-logo-computer-icons-user-user-blue-heroes-logo-thumbnail.png"
                radius="full"
                fallback="T"
              />
              <Box>
                <Text as="div" size="2" weight="bold">
                  {Fullname}
                </Text>
                <Text as="div" size="2" color="gray">
                  {Array.isArray(Role) ? Role.join(", ") : Role}
                </Text>
              </Box>
            </Flex>
          </Card>
        </Box>

        {/* Campo de Pesquisa */}
        <nav style={{ width: "-webkit-fill-available" }}>
          <Box className={styles.searchBox}>
            <TextField.Root
              size="2"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Pesquisar"
              style={{ width: "100%", padding: "2%", margin: "2%" }}
              className={styles.searchInput}
            >
              {searchTerm && (
                <Cross2Icon
                  className={styles.clearIcon}
                  onClick={() => setSearchTerm("")}
                />
              )}
              <TextField.Slot>
                <MagnifyingGlassIcon height="16" width="16" />
              </TextField.Slot>
            </TextField.Root>
          </Box>
          <nav className={styles.menuList}>
            <ul>
              {filteredMenuItems.map(
                (item, index) =>
                  hasAnyPermission(item.permissions) && (
                    <li key={index}>
                      {item.subItems ? (
                        <>
                          <button
                            onClick={() => toggleSubMenu(item.key)}
                            className={styles.menuItem}
                          >
                            {/* {item?.icon && <item.icon style={{marginRight:"5px"}}/>}  */}
                            {item.title}{" "}
                            {openSubMenu === item.key ? (
                              <ChevronDownIcon
                                style={{ verticalAlign: "middle" }}
                              />
                            ) : (
                              <ChevronUpIcon
                                style={{ verticalAlign: "middle" }}
                              />
                            )}
                          </button>
                          {openSubMenu === item.key && (
                            <ul className={styles.submenu}>
                              {item.subItems.map(
                                (subItem, subIndex) =>
                                  Permission &&
                                  Permission?.includes(subItem.permission) && (
                                    <li
                                      key={subIndex}
                                      style={{
                                        backgroundColor: isActive(
                                          subItem.route
                                        ),
                                      }}
                                    >
                                      <Link
                                        to={subItem.route}
                                        onClick={() => {
                                          setIsOpen(false);
                                          handleCleanReduxData(subItem.route);
                                        }}
                                      >
                                        {subItem.title}
                                      </Link>
                                    </li>
                                  )
                              )}
                            </ul>
                          )}
                        </>
                      ) : (
                        <></>
                      )}
                    </li>
                  )
              )}
            </ul>
          </nav>
        </nav>
      </div>
    </Box>
  );
};

export default Sidebar;
